import { useState } from 'react'

import { brownD, gauss, ru } from '../../lib/physics'
import { Predict, Slider, useFrames } from './common'

/**
 * Броуновская частица (урок 12): крупинка краски в воде под микроскопом мечется сама.
 * Её со всех сторон бьют молекулы воды, и случайно с одной стороны ударов то больше, то меньше.
 * Мелкая частица шустрее крупной, в горячей воде — шустрее, чем в холодной (D = kT / 6πηr).
 */

const ПОЛЕ = 20 // мкм — ширина поля зрения
const PX = 320 / ПОЛЕ // px на мкм
const УСКОРЕНИЕ = 2 // время на экране идёт вдвое быстрее
const СЛЕД = 240 // точек следа

interface Частица {
  x: number
  y: number
  след: [number, number][]
}

const центр = (): Частица => ({ x: ПОЛЕ / 2, y: ПОЛЕ / 4, след: [] })

export default function BrownLab() {
  const [rМкм, setR] = useState(0.5)
  const [t, setT] = useState(20)
  const [молекулы, setМолекулы] = useState(false)
  const [мелкая, setМелкая] = useState(центр)
  const [крупная, setКрупная] = useState<Частица>(() => ({ x: ПОЛЕ / 2, y: (3 * ПОЛЕ) / 4, след: [] }))
  const [время, setВремя] = useState(0)

  const RК = 3 * rМкм // крупная — втрое больше
  const D = (r: number) => brownD(r * 1e-6, t) * 1e12 // мкм²/с

  const шаг = (p: Частица, r: number, dt: number, уМин: number, уМакс: number): Частица => {
    const σ = Math.sqrt(2 * D(r) * dt)
    const x = Math.min(Math.max(p.x + σ * gauss(), r), ПОЛЕ - r)
    const y = Math.min(Math.max(p.y + σ * gauss(), уМин + r), уМакс - r)
    const след = [...p.след, [x, y] as [number, number]].slice(-СЛЕД)
    return { x, y, след }
  }

  useFrames((dt) => {
    const τ = dt * УСКОРЕНИЕ
    setМелкая((p) => шаг(p, rМкм, τ, 0, ПОЛЕ / 2))
    setКрупная((p) => шаг(p, RК, τ, ПОЛЕ / 2, ПОЛЕ))
    setВремя((v) => v + τ)
  })

  const сброс = () => {
    setМелкая(центр())
    setКрупная({ x: ПОЛЕ / 2, y: (3 * ПОЛЕ) / 4, след: [] })
    setВремя(0)
  }

  const путь = (p: Частица) => p.след.map(([x, y]) => `${x * PX},${y * PX}`).join(' ')
  const сдвиг = (p: Частица, y0: number) => Math.hypot(p.x - ПОЛЕ / 2, p.y - y0)
  // молекулы на рисунке — только чтобы показать удары; настоящие в тысячи раз мельче частицы
  const точки = молекулы
    ? Array.from({ length: 22 }, (_, i) => {
        const угол = (i / 22) * 2 * Math.PI + Math.random() * 0.2
        const d = rМкм * PX + 4 + Math.random() * 7
        return [мелкая.x * PX + d * Math.cos(угол), мелкая.y * PX + d * Math.sin(угол)]
      })
    : []

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 320 320" role="img" aria-label="Броуновские частицы под микроскопом">
        <rect x={0} y={0} width={320} height={320} rx={10} className="fill-cold" opacity={0.35} />
        <line x1={0} y1={160} x2={320} y2={160} className="ink-muted" strokeDasharray="3 5" />
        <polyline points={путь(мелкая)} fill="none" className="ink-el" strokeWidth={1} opacity={0.7} />
        <polyline points={путь(крупная)} fill="none" className="ink-heavy" strokeWidth={1} opacity={0.7} />
        {точки.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={1.6} className="fill-ink" opacity={0.6} />
        ))}
        <circle cx={мелкая.x * PX} cy={мелкая.y * PX} r={rМкм * PX} className="fill-el" />
        <circle cx={крупная.x * PX} cy={крупная.y * PX} r={RК * PX} className="fill-heavy" />
        <line x1={14} y1={306} x2={14 + 5 * PX} y2={306} className="ink" strokeWidth={2} />
        <text x={14 + 2.5 * PX} y={300} textAnchor="middle" className="lab-tick">5 мкм</text>
        <text x={8} y={16} className="lab-tick">мелкая, r = {ru(rМкм, 2)} мкм</text>
        <text x={8} y={176} className="lab-tick">крупная, r = {ru(RК, 2)} мкм</text>
      </svg>

      <div className="lab-readout">
        <span>прошло: {ru(время, 0)} с</span>
        <span className="el">мелкая ушла на {ru(сдвиг(мелкая, ПОЛЕ / 4), 1)} мкм</span>
        <span className="heavy">крупная — на {ru(сдвиг(крупная, (3 * ПОЛЕ) / 4), 1)} мкм</span>
      </div>

      <Slider label="Радиус мелкой частицы" value={rМкм} min={0.25} max={1.5} step={0.05} unit="мкм" shown={ru(rМкм, 2)} onChange={setR} />
      <Slider label="Температура воды" value={t} min={0} max={90} step={5} unit="°C" onChange={setT} />
      <div className="row-wrap">
        <button className={молекулы ? 'btn btn-primary' : 'btn'} onClick={() => setМолекулы((v) => !v)}>
          {молекулы ? 'Спрятать молекулы' : 'Показать удары молекул'}
        </button>
        <button className="btn" onClick={сброс}>Начать заново</button>
      </div>

      <Predict
        question="Какая частица мечется сильнее — мелкая или крупная?"
        options={['мелкая', 'крупная', 'одинаково']}
        right={0}
        revealed={время > 20}
        explain="Мелкая. Удары молекул с разных сторон случайно не уравновешиваются; лёгкую мелкую частицу перевес сдвигает заметнее — как щелчок сдвигает игрушечную машинку, а не коляску. Для тела размером с брусок перевес ничтожен — его броуновского движения не видно."
      />
      <p className="muted small">
        Броун в 1827 году увидел такое движение у пыльцы в воде и подумал, что она живая. Но так же мечутся пылинки в
        капле воды, запаянной в янтаре миллионы лет назад: дело не в жизни, а в ударах молекул. Теорию движения
        построил Эйнштейн в 1905 году.
      </p>
    </div>
  )
}
